import { useEffect, useMemo } from 'react'
import { View } from 'react-native'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { DEFAULT_REGION_POLICIES } from '@ca/common'
import Button from '@ui/buttons/Button'
import FormCheckboxSectionRow from '@ui/forms/Checkbox/FormCheckboxSectionRow'
import PolicyReadModal from '@user/customer/policies/PolicyReadModal'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import useCreateCustomerStore from '../useCreateCustomerStore'
import { getPoliciesStepSchema } from '../stepSchemas'
import useModalDataState from 'features/order/hooks/useModalDataState'
import useDateFormat from '@ui/hooks/useDateFormat'

interface Props {
  submitRef: React.MutableRefObject<() => void>
  onSuccess: () => void
  setIsPending?: (v: boolean) => void
}

export default function PoliciesStep({ submitRef, onSuccess, setIsPending }: Props) {
  const { t } = useTranslation(['ui', 'modals', 'deals-modals'])
  const config = useRemoteConfig()
  const { data, updateData } = useCreateCustomerStore()
  const contentModal = useModalDataState<string | null>({ data: null })
  const formatDate = useDateFormat({ dateStyle: 'medium' })

  const policies = config?.region.policies ?? DEFAULT_REGION_POLICIES
  const schema = useMemo(() => getPoliciesStepSchema(policies), [policies])

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: Object.fromEntries(
      policies.map((policy) => [
        policy.policy_key,
        !!data.policies?.[policy.policy_key],
      ])
    ),
  })

  const values = useWatch({ control: form.control })

  useEffect(() => {
    submitRef.current = form.handleSubmit(
      (formValues) => {
        updateData({ policies: formValues })
        onSuccess()
      },
      () => setIsPending?.(false)
    )
  })

  const activePolicy = contentModal.data
    ? policies.find((p) => p.policy_key === contentModal.data) ?? null
    : null

  return (
    <>
      <FormProvider {...form}>
        <View className="gap-8">
          {policies.map((policy) => (
            <FormCheckboxSectionRow
              key={policy.policy_key}
              name={policy.policy_key}
              required={policy.required}
              title={t(`deals-modals:policies.${policy.policy_key}.title`)}
              onValueChange={() => {
                if (contentModal.data === policy.policy_key) {
                  contentModal.close()
                }
              }}
              actionComponent={
                <Button
                  size="xs"
                  variant="outlined"
                  label={t('ui:read')}
                  onPress={() => contentModal.open(policy.policy_key)}
                />
              }
            />
          ))}
        </View>
      </FormProvider>

      <PolicyReadModal
        open={contentModal.isOpen}
        close={contentModal.close}
        policy={
          activePolicy
            ? {
                key: activePolicy.policy_key,
                required: activePolicy.required,
                revision: activePolicy.policy_revision,
                acceptedAt: null,
              }
            : null
        }
        isDraftAccepted={!!values[contentModal.data ?? '']}
        onAccept={() => {
          if (!contentModal.data) {
            return
          }
          form.setValue(contentModal.data, true, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          })
          contentModal.close()
        }}
        formatDate={formatDate}
      />
    </>
  )
}
